/* ============================================================
   Toee Tire — the site-wide Current vehicle.
   One Vehicle + Fitment the shopper picked (homepage Vehicle tab, Account
   "Shop again"), kept in localStorage so the header, collections, product
   page and cart can all read it. Small and dependency-free on purpose: the
   header loads this on every page, never the Vehicle lookup (toee-vehicle-lookup.js).

   Pure helpers + createStore are exported for tests
   (tests/toee-vehicle-current.test.js); `store` is the live one in a browser.
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    // The header and the homepage assets both load this file: keep the first copy, so the
    // chip and the lookup share one store (and one set of change listeners).
    root.ToeeVehicleCurrent = root.ToeeVehicleCurrent || api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var KEY = 'toee-current-vehicle';

  /** Vehicle data writes an LT size with a trailing C; dealers read it as LT. */
  function displaySize(size) {
    var s = String(size || '');
    return /C$/.test(s) ? 'LT' + s.slice(0, -1) : s;
  }

  function fitmentChip(f) {
    var sizes = [displaySize(f.front.tireSize)];
    if (f.rear) sizes.push(displaySize(f.rear.tireSize));
    var d = Number(f.diameterDeltaIn) || 0;
    return {
      sizes: sizes.join(' · '),
      inches: f.front.rimDiameterIn + '″',
      tag: f.sizing === 'oe' ? '' : (d > 0 ? '+' + d : '−' + Math.abs(d)) + '″',
    };
  }

  function axle(a) {
    return a ? { tireSize: a.tireSize, rimDiameterIn: a.rimDiameterIn } : null;
  }

  /** A Fitment as stored: the numbers the site reads, without the tire / wheel variant lists. */
  function slimFitment(f) {
    return {
      id: f.id,
      sizing: f.sizing,
      diameterDeltaIn: f.diameterDeltaIn,
      staggered: f.staggered,
      boltPattern: f.boltPattern,
      hubBoreMm: f.hubBoreMm,
      rearHubBoreMm: f.rearHubBoreMm,
      front: axle(f.front),
      rear: axle(f.rear),
    };
  }

  /** q = the picked slugs/names (year, make, model, trim, makeName, modelName); body = /vehicle/fitment's answer. */
  function fromFitmentResponse(q, body, fitmentId, nowIso) {
    var v = (body && body.vehicle) || {};
    return {
      v: 1,
      year: q.year,
      make: q.make,
      model: q.model,
      trim: q.trim,
      makeName: q.makeName,
      modelName: q.modelName,
      label: v.label,
      market: v.market,
      fitments: ((body && body.fitments) || []).map(slimFitment),
      fitmentId: fitmentId,
      savedAt: nowIso,
    };
  }

  function parseStored(text) {
    if (text == null) return null;
    var v;
    try {
      v = JSON.parse(text);
    } catch (e) {
      return null;
    }
    if (!v || typeof v !== 'object' || v.v !== 1) return null;
    if (!v.year || !v.make || !v.model || !v.trim || !v.label || !v.fitmentId || !Array.isArray(v.fitments)) return null;
    var has = v.fitments.some(function (f) { return f && f.id === v.fitmentId; });
    return has ? v : null;
  }

  /** `preferred` when the list has it, else the first id. */
  function pickFitmentId(fitments, preferred) {
    var list = fitments || [];
    for (var i = 0; i < list.length; i++) if (list[i].id === preferred) return preferred;
    return list.length ? list[0].id : null;
  }

  function currentFitment(vehicle) {
    var list = (vehicle && vehicle.fitments) || [];
    for (var i = 0; i < list.length; i++) if (list[i].id === vehicle.fitmentId) return list[i];
    return null;
  }

  /** "CX-5 ’16". Account "Shop again" has only slugs, so fall back to the model slug upper-cased. */
  function shortLabel(vehicle) {
    var name = vehicle.modelName || String(vehicle.model || '').toUpperCase();
    return name + ' ’' + String(vehicle.year).slice(-2);
  }

  /** "225/65R17", or "F 225/45R18 · R 255/40R18" when the rear differs. */
  function sizeText(fitment) {
    var front = displaySize(fitment.front.tireSize);
    if (fitment.staggered && fitment.rear) return 'F ' + front + ' · R ' + displaySize(fitment.rear.tireSize);
    return front;
  }

  function vehicleId(vehicle) {
    return [vehicle.year, vehicle.make, vehicle.model, vehicle.trim, vehicle.fitmentId].join('|');
  }

  function parseVehicleId(text) {
    var p = String(text == null ? '' : text).split('|');
    if (p.length !== 5 || p.some(function (x) { return !x; })) return null;
    return { year: p[0], make: p[1], model: p[2], trim: p[3], fitmentId: p[4] };
  }

  /**
   * Account "Shop this vehicle again": the /fitment query and the Fitment id from an order's `Vehicle ID`,
   * or null when the id does not parse.
   */
  function shopAgainQuery(idText) {
    var p = parseVehicleId(idText);
    if (!p) return null;
    return {
      query: ['year', 'make', 'model', 'trim'].map(function (k) { return k + '=' + encodeURIComponent(p[k]); }).join('&'),
      fitmentId: p.fitmentId,
    };
  }

  /**
   * What the order carries: "2016 Mazda CX-5 2.5i · GT · 225/55R19" for people and the five-part id for
   * the account page. No vehicle (or no current Fitment to name a size) clears both.
   */
  function cartAttributes(vehicle) {
    var f = currentFitment(vehicle);
    if (!f) return { Vehicle: '', 'Vehicle ID': '' };
    return { Vehicle: vehicle.label + ' · ' + sizeText(f), 'Vehicle ID': vehicleId(vehicle) };
  }

  /** The attributes to write when the cart ({vehicle, vehicleId} as rendered) differs from the vehicle, else null. */
  function cartSync(cart, vehicle) {
    var want = cartAttributes(vehicle);
    var have = cart || {};
    if ((have.vehicle || '') === want.Vehicle && (have.vehicleId || '') === want['Vehicle ID']) return null;
    return want;
  }

  /**
   * The Current vehicle in `storage` (localStorage, or null), told about other tabs via `win`.
   * Storage that throws or is missing behaves as empty; the last pick is still kept in memory
   * so it works for the rest of this page.
   */
  function createStore(storage, win) {
    var mem = null;
    var listeners = [];

    function notify(v) {
      listeners.forEach(function (fn) {
        try { fn(v); } catch (e) { /* one bad listener must not block the rest */ }
      });
    }

    function read() {
      var text = null;
      try {
        if (!storage) return mem;
        text = storage.getItem(KEY);
      } catch (e) {
        return mem; // storage unusable: the last pick on this page
      }
      if (text == null) {
        mem = null; // a working storage with no key means no vehicle (cleared elsewhere)
        return null;
      }
      var v = parseStored(text);
      if (!v) {
        try { storage.removeItem(KEY); } catch (e) { /* nothing to clean */ }
      }
      mem = v;
      return v;
    }

    function save(v) {
      // A record read() would drop is never stored: it would silently clear the Current vehicle.
      if (!parseStored(JSON.stringify(v))) return;
      mem = v;
      try { if (storage) storage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* kept in memory */ }
      notify(v);
    }

    function clear() {
      mem = null;
      try { if (storage) storage.removeItem(KEY); } catch (e) { /* kept in memory */ }
      notify(null);
    }

    if (win && win.addEventListener) {
      win.addEventListener('storage', function (e) {
        if (!e || (e.key !== KEY && e.key !== null)) return; // key null = storage.clear()
        mem = e.key === null ? null : parseStored(e.newValue);
        notify(mem);
      });
    }

    return {
      read: read,
      save: save,
      clear: clear,
      onChange: function (fn) { listeners.push(fn); },
    };
  }

  var store = null;
  if (typeof window !== 'undefined') {
    var ls = null;
    try { ls = window.localStorage || null; } catch (e) { ls = null; }
    store = createStore(ls, window);
  }

  return {
    KEY: KEY,
    parseStored: parseStored,
    fromFitmentResponse: fromFitmentResponse,
    pickFitmentId: pickFitmentId,
    currentFitment: currentFitment,
    shortLabel: shortLabel,
    displaySize: displaySize,
    fitmentChip: fitmentChip,
    sizeText: sizeText,
    vehicleId: vehicleId,
    parseVehicleId: parseVehicleId,
    shopAgainQuery: shopAgainQuery,
    cartAttributes: cartAttributes,
    cartSync: cartSync,
    createStore: createStore,
    store: store,
  };
});
